import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Resource } from '@/types';

interface ResourceOverviewPanelProps {
  resources: Resource[];
}

export function ResourceOverviewPanel({ resources }: ResourceOverviewPanelProps) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available':
        return 'bg-green-500';
      case 'busy':
        return 'bg-red-500';
      case 'off':
        return 'bg-yellow-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'permanent':
        return 'Permanent';
      case 'contractor':
        return 'Contractor';
      case 'agency':
        return 'Agency';
      default:
        return type;
    }
  };

  const getTypeVariant = (type: string): "default" | "secondary" | "outline" => {
    switch (type) {
      case 'permanent':
        return 'default';
      case 'contractor':
        return 'secondary';
      case 'agency':
        return 'outline';
      default:
        return 'default';
    }
  };

  const permanentCount = resources.filter((r) => r.type === 'permanent').length;
  const contractorCount = resources.filter((r) => r.type === 'contractor').length;
  const agencyCount = resources.filter((r) => r.type === 'agency').length;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Resource Overview</CardTitle>
        <CardDescription>
          Complete list of all team members and their current status
        </CardDescription>
        <div className="flex gap-4 mt-4">
          <Badge variant="default">Permanent: {permanentCount}</Badge>
          <Badge variant="secondary">Contractors: {contractorCount}</Badge>
          <Badge variant="outline">Agency: {agencyCount}</Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {resources.map((resource) => {
            const initials = resource.name
              .split(' ')
              .map((n) => n[0])
              .join('')
              .toUpperCase();

            return (
              <div
                key={resource.id}
                className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    <AvatarFallback>{initials}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium">{resource.name}</p>
                    <p className="text-sm text-muted-foreground">{resource.role}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant={getTypeVariant(resource.type)}>
                    {getTypeLabel(resource.type)}
                  </Badge>
                  <div className="flex items-center gap-2">
                    <div className={`h-3 w-3 rounded-full ${getStatusColor(resource.status)}`} />
                    <span className="text-sm capitalize">{resource.status}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
